import React from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, MoreVertical } from 'lucide-react';
import { KanbanProductCard } from './KanbanProductCard';

interface KanbanColumnData {
  id: string;
  title: string;
  description: string;
  color: string;
  items: any[];
  maxItems?: number;
}

interface KanbanColumnProps {
  column: KanbanColumnData;
  participants: any[];
  dragOverColumn: string | null;
  isDragDisabled: boolean;
  cartId: string;
  currentUserId?: string;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  participants,
  dragOverColumn,
  isDragDisabled,
  cartId,
  currentUserId,
}) => {
  const isOver = dragOverColumn === column.id;
  const isAtLimit = column.maxItems && column.items.length >= column.maxItems;
  
  return (
    <div className="flex-shrink-0 w-80">
      {/* Column Header */}
      <div className="flex items-center justify-between mb-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: column.color }}
          />
          <div>
            <h3 className="font-medium text-gray-900">{column.title}</h3>
            <p className="text-xs text-gray-500">{column.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">
            {column.items.length}
            {column.maxItems && `/${column.maxItems}`}
          </span>
          <button className="p-1 rounded hover:bg-gray-100 transition-colors">
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>
      
      {/* Droppable Column */}
      <Droppable droppableId={column.id} isDropDisabled={!!isAtLimit}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-96 p-3 rounded-lg border-2 border-dashed transition-all ${
              snapshot.isDraggingOver || isOver
                ? 'border-blue-300 bg-blue-50'
                : isAtLimit
                ? 'border-red-300 bg-red-50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            {/* Column Items */}
            <div className="space-y-3">
              {column.items.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  isDragDisabled={isDragDisabled}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`transition-all ${
                        snapshot.isDragging
                          ? 'transform rotate-2 scale-105 shadow-xl z-50'
                          : 'hover:shadow-md'
                      }`}
                      style={{
                        ...provided.draggableProps.style,
                        ...(snapshot.isDragging && {
                          background: 'white',
                          borderRadius: '8px',
                        }),
                      }}
                    >
                      <KanbanProductCard
                        item={item}
                        participants={participants}
                        isDragDisabled={isDragDisabled}
                        columnColor={column.color}
                        isDragging={snapshot.isDragging}
                        cartId={cartId}
                        currentUserId={currentUserId}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
            </div>
            
            {provided.placeholder}
            
            {/* Empty State */}
            {column.items.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <div className="text-sm">
                  Drop products here to add them to
                  <br />
                  <strong>{column.title.toLowerCase()}</strong>
                </div>
              </div>
            )}
            
            {/* At Limit Warning */}
            {isAtLimit && (
              <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-center">
                <p className="text-xs text-red-600">
                  Column is at capacity ({column.maxItems} items)
                </p>
              </div>
            )}
          </div>
        )}
      </Droppable>
      
      {/* Quick Actions */}
      <div className="mt-3 flex gap-2">
        <button className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-xs text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors">
          <Plus className="w-3 h-3" />
          Add Product
        </button>
      </div>
    </div>
  );
};